import { useEffect, useRef, useState } from "react";
import { useLoaderData, useLocation, useNavigate } from "@remix-run/react";
import { json, LoaderFunctionArgs } from "@remix-run/node";
import { Filter, MapPin, Search } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { ProductCard } from "../components/ProductCard";
import Header from "../components/header";
import { getUser } from "../lib/database_interfaces/user";
import { getSupabaseWithSessionHeaders } from "../lib/supabase_server";
import { PUBLIC_STORE_PROFILES } from "../lib/public_store_profiles";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Separator } from "../components/ui/separator";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "../components/ui/sheet";
import { useToast } from "../hooks/use-toast";

import {
  calculateHaversineDistance,
  getCoordinatesFromZipCode,
} from "../lib/helpers/storehelpers";

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const url = new URL(request.url);
  const query = url.searchParams.get("q")?.trim() ?? "";
  const view = url.searchParams.get("view")?.trim().toLowerCase() ?? "";
  const searchView =
    view === "trending" || view === "specials"
      ? (view as "trending" | "specials")
      : null;

  const { session, supabase } = await getSupabaseWithSessionHeaders({
    request,
  });

  let user;
  if (session?.user) {
    user = await getUser({ userId: session.user.id, dbClient: supabase });
  }
  const categoryQuery = supabase.from("Categories").select("*");
  const [categoryRes] = await Promise.all([categoryQuery]);
  if (categoryRes.error) {
    throw new Response(categoryRes.error?.message, {
      status: 500,
    });
  }

  const isValidCategory = (categoryName: string): boolean => {
    return categoryRes.data.some(
      (category) => category.name.toLowerCase() === categoryName.toLowerCase(),
    );
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let items: any[] = [];

  if (searchView === "trending") {
    const { data: rpcProducts, error: rpcError } = await supabase.rpc(
      "get_recently_sold_products",
      { p_limit: 120 },
    );
    if (rpcError) {
      console.error("Error fetching trending products:", rpcError);
    }
    const products = [...(rpcProducts ?? [])];
    if (products.length < 30) {
      const { data: fillProducts, error: fillError } = await supabase
        .from("Products")
        .select("*")
        .eq("status", "ACTIVE")
        .limit(80);
      if (fillError) {
        console.error("Error fetching products for trending fill:", fillError);
      }
      if (fillProducts?.length) {
        const seen = new Set(products.map((p: { id: string }) => p.id));
        for (const p of fillProducts) {
          if (!seen.has(p.id)) {
            seen.add(p.id);
            products.push(p);
          }
        }
      }
    }
    items = products;
  } else if (searchView === "specials") {
    const { data, error } = await supabase
      .from("Products")
      .select("*")
      .eq("status", "ACTIVE")
      .not("markdown", "is", null);
    if (error) throw new Response(error.message, { status: 500 });
    items = data ?? [];
  } else if (query.trim() === "") {
    const { data, error } = await supabase
      .from("Products")
      .select("*")
      .eq("status", "ACTIVE");
    if (error) throw new Response(error.message, { status: 500 });
    items = data ?? [];
  } else {
    const nameQuery = supabase
      .from("Products")
      .select("*")
      .ilike("name", `%${query}%`)
      .eq("status", "ACTIVE");

    if (isValidCategory(query)) {
      const catQuery = supabase
        .from("Products")
        .select("*")
        .not("categories", "is", null)
        .contains("categories", [query])
        .eq("status", "ACTIVE");

      const [nameRes, catRes] = await Promise.all([nameQuery, catQuery]);
      if (nameRes.error) throw new Response(nameRes.error.message, { status: 500 });
      if (catRes.error) throw new Response(catRes.error.message, { status: 500 });

      const seen = new Set<string>();
      for (const p of [...(nameRes.data ?? []), ...(catRes.data ?? [])]) {
        if (!seen.has(p.id)) {
          seen.add(p.id);
          items.push(p);
        }
      }
    } else {
      const { data, error } = await nameQuery;
      if (error) throw new Response(error.message, { status: 500 });
      items = data ?? [];
    }
  }

  // Extract unique store IDs from the products
  const storeIds = [
    ...new Set(items.map((product) => product.store_id)),
  ];

  // Fetch only active stores for the store IDs from products
  let storesRes;
  if (storeIds.length > 0) {
    storesRes = await supabase
      .from(PUBLIC_STORE_PROFILES)
      .select("id, name, slug, summary, zip, banner_pic")
      .in("id", storeIds);
  } else {
    storesRes = { data: [], error: null };
  }

  if (storesRes.error) {
    throw new Response(storesRes.error?.message, {
      status: 500,
    });
  }

  // Build a set of active store IDs to filter products
  const activeStoreIds = new Set(storesRes.data.map((store) => store.id));

  const storeIdToNameMap = new Map(
    storesRes.data.map((store) => [store.id, store.name]),
  );

  // Only include products from active stores
  const taggedItems = items
    .filter((product) => activeStoreIds.has(product.store_id))
    .map((product) => {
      const storeName = storeIdToNameMap.get(product.store_id) ||
        "Unknown Store";
      return {
        ...product,
        type: "product",
        store_name: storeName,
      };
    });

  const taggedStores = storesRes.data.map((i) => {
    return { ...i, type: "supplier" };
  });

  return json({
    items: taggedItems || [],
    stores: taggedStores || [],
    userDetails: user || undefined,
    categories: categoryRes.data || [],
    searchView,
  });
};

export default function SearchResults() {
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { items, userDetails, stores, categories, searchView } = useLoaderData<
    typeof loader
  >();

  // Parse the q parameter from URL
  const urlParams = new URLSearchParams(location.search);
  const initialQuery = urlParams.get("q") || "";
  const resultsTitle =
    searchView === "trending"
      ? "Trending products"
      : searchView === "specials"
        ? "Supplier specials"
        : "Search results";

  const [query, setQuery] = useState(initialQuery);
  const [initialQueryFromUrl, setInitialQueryFromUrl] = useState(initialQuery);
  const [filteredProducts, setFilteredProducts] = useState(items);
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const [supplierDropdownOpen, setSupplierDropdownOpen] = useState(false);
  const [sortBy, setSortBy] = useState("relevance");
  const [categorySearchTerm, setCategorySearchTerm] = useState("");
  const [supplierSearchTerm, setSupplierSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(24);
  const [searchFilter, setSearchFilter] = useState(""); // For filtering displayed results

  // Filter states
  const [selectedCategories, setSelectedCategories] = useState<string>("");
  const [selectedSuppliers, setSelectedSuppliers] = useState<string[]>([]);
  const [minRating, setMinRating] = useState(0);
  const [inStockOnly, setInStockOnly] = useState(false);
  const [maxDistance, setMaxDistance] = useState<string>("0"); // "0" = no filter

  // Helper function to calculate distance between two zip codes using API and Haversine formula
  const calculateDistance = async (
    zip1: string,
    zip2: string,
  ): Promise<number> => {
    if (!zip1 || !zip2) return Infinity;

    try {
      const [coords1, coords2] = await Promise.all([
        getCoordinatesFromZipCode(zip1),
        getCoordinatesFromZipCode(zip2),
      ]);

      if (!coords1 || !coords2) {
        console.warn(
          `Could not get coordinates for zip codes: ${zip1}, ${zip2}`,
        );
        return Infinity;
      }

      const distance = calculateHaversineDistance(
        coords1.lat,
        coords1.lng,
        coords2.lat,
        coords2.lng,
      );

      return distance;
    } catch (error) {
      console.warn(
        `Error calculating distance between ${zip1} and ${zip2}:`,
        error,
      );
      return Infinity;
    }
  };

  // Helper function to validate if a category exists
  const isValidCategory = (categoryName: string): boolean => {
    return categories.some(
      (category) => category.name.toLowerCase() === categoryName.toLowerCase(),
    );
  };

  // Reset maxDistance to "0" when main_zip is not set
  useEffect(() => {
    if (!userDetails?.main_zip && maxDistance !== "0") {
      setMaxDistance("0");
    }
  }, [userDetails?.main_zip, maxDistance]);

  // Initialize selectedCategories from URL
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const queryFromUrl = urlParams.get("q");

    if (queryFromUrl && categories.length > 0) {
      // Check if the query from URL exists in the categories list
      if (isValidCategory(queryFromUrl)) {
        setSelectedCategories(queryFromUrl.toLowerCase());
        setQuery(""); // Clear query when it's a category
        setInitialQueryFromUrl(""); // No initial query when it's a category
      } else {
        // If query is not a category, set it as search query
        setSelectedCategories("");
        setQuery(queryFromUrl);
        setInitialQueryFromUrl(queryFromUrl); // Set as initial query
      }
    } else if (!queryFromUrl) {
      setSelectedCategories("");
      setQuery("");
      setInitialQueryFromUrl("");
    }
  }, [location.search, categories, navigate, toast]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [
    selectedCategories,
    selectedSuppliers,
    minRating,
    inStockOnly,
    maxDistance,
    sortBy,
    searchFilter,
  ]);

  // Apply filters
  useEffect(() => {
    const applyFilters = async () => {
      // Create a mapping from store names to store IDs
      const storeNameToIdMap = new Map(
        stores.map((store) => [store.name, store.id]),
      );

      // Cache for distance calculations to avoid recalculating the same distances
      const distanceCache = new Map<string, number>();

      const filtered = await Promise.all(
        items.map(async (product) => {
          // Text search - only apply if there's a query
          const matchesQuery = query === "" ||
            product.name.toLowerCase().includes(query.toLowerCase());

          // Search filter - filter displayed results
          const matchesSearchFilter = searchFilter === "" ||
            product.name.toLowerCase().includes(searchFilter.toLowerCase()) ||
            product.store_name
              .toLowerCase()
              .includes(searchFilter.toLowerCase());

          // Category filter — also include products whose name matches
          const matchesCategory = selectedCategories.length === 0 ||
            product.categories?.includes(
              selectedCategories.toLocaleLowerCase(),
            ) ||
            product.name.toLowerCase().includes(
              selectedCategories.toLowerCase(),
            );

          // Supplier filter - convert store names to store IDs for comparison
          const selectedStoreIds = selectedSuppliers
            .map((supplierName) => storeNameToIdMap.get(supplierName))
            .filter(Boolean);
          const matchesSupplier = selectedSuppliers.length === 0 ||
            selectedStoreIds.includes(product.store_id);

          // Calculate distance for all products (needed for sorting)
          let distance = Infinity;
          const userZip = userDetails?.main_zip;
          const store = stores.find((s) => s.id === product.store_id);
          const storeZip = store?.zip;

          if (userZip && storeZip) {
            const cacheKey = `${userZip}-${storeZip}`;
            if (distanceCache.has(cacheKey)) {
              distance = distanceCache.get(cacheKey)!;
            } else {
              distance = await calculateDistance(
                String(userZip),
                String(storeZip),
              );
              distanceCache.set(cacheKey, distance);
            }
          }

          // Distance filter
          let matchesDistance = true;
          if (maxDistance !== "0") {
            matchesDistance = distance <= parseInt(maxDistance);
          }

          //   // Rating filter
          //   const matchesRating = product.rating >= minRating;

          // Stock filter
          // const matchesStock = !inStockOnly || product.inStock;

          return {
            product: {
              ...product,
              distance: distance,
            },
            matches: matchesQuery &&
              matchesSearchFilter &&
              matchesCategory &&
              matchesSupplier &&
              matchesDistance,
          };
        }),
      );

      const filteredProducts = filtered
        .filter((item) => item.matches)
        .map((item) => item.product);

      // Apply sorting
      switch (sortBy) {
        case "price-low":
          filteredProducts.sort((a, b) => a.price - b.price);
          break;
        case "price-high":
          filteredProducts.sort((a, b) => b.price - a.price);
          break;
        default:
          // Sort by distance by default when there's a query, otherwise keep original order
          if (query.trim() !== "") {
            filteredProducts.sort((a, b) => {
              const distanceA =
                (a as typeof a & { distance: number }).distance || Infinity;
              const distanceB =
                (b as typeof b & { distance: number }).distance || Infinity;
              return distanceA - distanceB;
            });
          }
          break;
      }

      setFilteredProducts(filteredProducts);
    };

    applyFilters();
  }, [
    query,
    searchFilter,
    selectedCategories,
    selectedSuppliers,
    minRating,
    inStockOnly,
    maxDistance,
    sortBy,
    items,
    stores,
    userDetails,
  ]);

  // Pagination logic
  const totalItems = filteredProducts.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedProducts = filteredProducts.slice(startIndex, endIndex);

  // Pagination component
  const Pagination = () => {
    if (totalPages <= 1) return null;

    const handlePageChange = (newPage: number) => {
      setCurrentPage(newPage);
    };

    const getPageNumbers = () => {
      const pages: (number | string)[] = [];
      const maxVisiblePages = 5;

      if (totalPages <= maxVisiblePages) {
        for (let i = 1; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        if (currentPage <= 3) {
          for (let i = 1; i <= 4; i++) {
            pages.push(i);
          }
          pages.push("...");
          pages.push(totalPages);
        } else if (currentPage >= totalPages - 2) {
          pages.push(1);
          pages.push("...");
          for (let i = totalPages - 3; i <= totalPages; i++) {
            pages.push(i);
          }
        } else {
          pages.push(1);
          pages.push("...");
          for (let i = currentPage - 1; i <= currentPage + 1; i++) {
            pages.push(i);
          }
          pages.push("...");
          pages.push(totalPages);
        }
      }

      return pages;
    };

    return (
      <div className="flex flex-col sm:flex-row items-center justify-between mt-6 gap-4">
        <div className="text-sm text-muted-foreground text-center sm:text-left">
          Showing {startIndex + 1} to {Math.min(endIndex, totalItems)} of{" "}
          {totalItems} results
        </div>
        <div className="flex items-center gap-1 sm:gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="text-xs sm:text-sm"
          >
            <span className="hidden sm:inline">Previous</span>
            <span className="sm:hidden">Prev</span>
          </Button>

          <div className="flex items-center gap-1">
            {getPageNumbers().map((page, index) => (
              <div key={index}>
                {page === "..."
                  ? (
                    <span className="px-2 sm:px-3 py-2 text-xs sm:text-sm text-muted-foreground">
                      ...
                    </span>
                  )
                  : (
                    <Button
                      variant={currentPage === page ? "default" : "outline"}
                      size="sm"
                      onClick={() => handlePageChange(page as number)}
                      className="w-7 h-7 sm:w-8 sm:h-8 p-0 text-xs sm:text-sm"
                    >
                      {page}
                    </Button>
                  )}
              </div>
            ))}
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              handlePageChange(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            className="text-xs sm:text-sm"
          >
            Next
          </Button>
        </div>
      </div>
    );
  };

  const handleCategoryToggle = (category: string) => {
    // Validate the category before setting it
    if (category && !isValidCategory(category)) {
      console.warn(`Invalid category: ${category}`);
      toast({
        title: "Invalid Category",
        description: `The category "${category}" is not available.`,
        variant: "destructive",
      });
      return;
    }

    setSelectedCategories(category.toLowerCase());
    setQuery(category);
    if (category) {
      navigate(`?q=${encodeURIComponent(category.toLocaleLowerCase())}`);
    } else {
      navigate("?q=");
    }
  };

  const handleSupplierToggle = (supplier: string) => {
    if (selectedSuppliers.includes(supplier)) {
      setSelectedSuppliers(selectedSuppliers.filter((s) => s !== supplier));
    } else {
      setSelectedSuppliers([...selectedSuppliers, supplier]);
    }
  };

  const handleQueryClear = () => {
    setQuery("");
    setInitialQueryFromUrl("");
    navigate("?q=");
  };

  const clearAllFilters = () => {
    setSelectedCategories("");
    setSelectedSuppliers([]);
    setMinRating(0);
    setInStockOnly(false);
    setMaxDistance("0");
    setSearchFilter("");
    handleQueryClear();
  };

  const categoryInputRef = useRef<HTMLInputElement>(null);
  const supplierInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (categoryDropdownOpen && categoryInputRef.current) {
      categoryInputRef.current.focus();
    }
  }, [categoryDropdownOpen, categorySearchTerm]);

  useEffect(() => {
    if (supplierDropdownOpen && supplierInputRef.current) {
      supplierInputRef.current.focus();
    }
  }, [supplierDropdownOpen, supplierSearchTerm]);

  const FilterSidebar = () => (
    <div className="space-y-4 sm:space-y-6">
      {/* Categories */}
      <div>
        <h3 className="font-semibold mb-2 sm:mb-3 text-sm sm:text-base">
          Product/Brand
        </h3>

        {/* Mobile/Tablet: Use Select */}
        <div className="lg:hidden">
          <Select
            value={selectedCategories}
            onValueChange={handleCategoryToggle}
          >
            <SelectTrigger className="w-full text-sm">
              <SelectValue placeholder="Select categories..." />
            </SelectTrigger>
            <SelectContent>
              {categories.map((category) => (
                <SelectItem
                  key={category.id}
                  value={category.name.toLowerCase()}
                >
                  {category.name.toLowerCase()}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Desktop: Use Custom Searchable Dropdown */}
        <div className="hidden lg:block">
          <div className="relative">
            <button
              type="button"
              onClick={() => setCategoryDropdownOpen(!categoryDropdownOpen)}
              className="w-full flex items-center justify-between px-3 py-2 text-sm border border-input bg-background rounded-md hover:bg-accent hover:text-accent-foreground"
            >
              {selectedCategories.length > 0
                ? selectedCategories
                : "Select categories..."}
              <svg
                className="ml-2 h-4 w-4 opacity-50"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>

            {categoryDropdownOpen && (
              <div className="absolute top-full left-0 right-0 z-50 mt-1 border border-input bg-background rounded-md shadow-lg">
                <div className="p-2 border-b">
                  <input
                    ref={categoryInputRef}
                    type="text"
                    placeholder="Search categories..."
                    value={categorySearchTerm}
                    onChange={(e) => setCategorySearchTerm(e.target.value)}
                    className="w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
                <div className="max-h-[200px] overflow-y-auto">
                  {categories
                    .filter((cat) =>
                      cat.name
                        .toLowerCase()
                        .includes(categorySearchTerm.toLowerCase())
                    )
                    .map((category) => (
                      <button
                        key={category.id}
                        type="button"
                        onClick={() => {
                          handleCategoryToggle(category.name);
                          setCategoryDropdownOpen(false);
                          setCategorySearchTerm("");
                        }}
                        className="w-full px-3 py-2 text-sm text-left hover:bg-accent hover:text-accent-foreground flex items-center"
                      >
                        {selectedCategories === category.name.toLowerCase() && (
                          <svg
                            className="mr-2 h-4 w-4"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        )}
                        {category.name.toLowerCase()}
                      </button>
                    ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Selected Categories Display */}
        {selectedCategories.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            <Badge
              key={selectedCategories}
              variant="secondary"
              className="cursor-pointer text-xs"
              onClick={() => handleCategoryToggle("")}
            >
              {selectedCategories} ×
            </Badge>
          </div>
        )}
      </div>

      <Separator />

      {/* Suppliers */}
      <div>
        <h3 className="font-semibold mb-2 sm:mb-3 text-sm sm:text-base">
          Suppliers
        </h3>

        {/* Mobile/Tablet: Use Select */}
        <div className="lg:hidden">
          <Select
            value={selectedSuppliers.length > 0 ? selectedSuppliers[0] : ""}
            onValueChange={(value) => {
              if (value && !selectedSuppliers.includes(value)) {
                setSelectedSuppliers([...selectedSuppliers, value]);
              }
            }}
          >
            <SelectTrigger className="w-full text-sm">
              <SelectValue placeholder="Select suppliers..." />
            </SelectTrigger>
            <SelectContent>
              {stores.map((supplier) => (
                <SelectItem key={supplier.name} value={supplier.name}>
                  {supplier.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Desktop: Use Custom Searchable Dropdown */}
        <div className="hidden lg:block">
          <div className="relative">
            <button
              type="button"
              onClick={() => setSupplierDropdownOpen(!supplierDropdownOpen)}
              className="w-full flex items-center justify-between px-3 py-2 text-sm border border-input bg-background rounded-md hover:bg-accent hover:text-accent-foreground"
            >
              {selectedSuppliers.length > 0
                ? selectedSuppliers.length === 1
                  ? selectedSuppliers[0]
                  : `${selectedSuppliers.length} suppliers selected`
                : "Select suppliers..."}
              <svg
                className="ml-2 h-4 w-4 opacity-50"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>

            {supplierDropdownOpen && (
              <div className="absolute top-full left-0 right-0 z-50 mt-1 border border-input bg-background rounded-md shadow-lg">
                <div className="p-2 border-b">
                  <input
                    ref={supplierInputRef}
                    type="text"
                    placeholder="Search suppliers..."
                    value={supplierSearchTerm}
                    onChange={(e) => setSupplierSearchTerm(e.target.value)}
                    className="w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
                <div className="max-h-[200px] overflow-y-auto">
                  {stores
                    .filter((supplier) =>
                      supplier.name
                        .toLowerCase()
                        .includes(supplierSearchTerm.toLowerCase())
                    )
                    .map((supplier) => (
                      <button
                        key={supplier.name}
                        type="button"
                        onClick={() => {
                          handleSupplierToggle(supplier.name);
                          setSupplierSearchTerm("");
                        }}
                        className="w-full px-3 py-2 text-sm text-left hover:bg-accent hover:text-accent-foreground flex items-center"
                      >
                        {selectedSuppliers.includes(supplier.name) && (
                          <svg
                            className="mr-2 h-4 w-4"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        )}
                        {supplier.name}
                      </button>
                    ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Selected Suppliers Display */}
        {selectedSuppliers.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {selectedSuppliers.map((supplier) => (
              <Badge
                key={supplier}
                variant="secondary"
                className="cursor-pointer text-xs sm:text-sm"
                onClick={() => handleSupplierToggle(supplier)}
              >
                {supplier} ×
              </Badge>
            ))}
          </div>
        )}
      </div>

      <Separator />

      {/* Distance Filter */}
      <div>
        <h3 className="font-semibold mb-2 sm:mb-3 text-sm sm:text-base flex items-center">
          <MapPin className="h-4 w-4 mr-2" />
          Maximum Distance
        </h3>
        <Select
          value={maxDistance}
          onValueChange={setMaxDistance}
          disabled={!userDetails?.main_zip}
        >
          <SelectTrigger
            className="w-full text-sm"
            disabled={!userDetails?.main_zip}
          >
            <SelectValue placeholder="Select maximum distance..." />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="0">No limit</SelectItem>
            <SelectItem value="10">Within 10 miles</SelectItem>
            <SelectItem value="25">Within 25 miles</SelectItem>
            <SelectItem value="50">Within 50 miles</SelectItem>
            <SelectItem value="100">Within 100 miles</SelectItem>
            <SelectItem value="250">Within 250 miles</SelectItem>
          </SelectContent>
        </Select>
        {!userDetails?.main_zip
          ? (
            <p className="text-xs text-muted-foreground mt-1">
              Please set your zip code in your account settings to use distance
              filtering.
            </p>
          )
          : (
            maxDistance !== "0" && (
              <p className="text-xs text-muted-foreground mt-1">
                Based on your zip code: {userDetails.main_zip}
              </p>
            )
          )}
      </div>

      <Separator />

      {/* Rating */}
      {
        /* <div>
        <h3 className="font-semibold mb-2 sm:mb-3 text-sm sm:text-base">
          Minimum Rating
        </h3>
        <div className="space-y-2">
          {[4, 3, 2, 1].map((rating) => (
            <div key={rating} className="flex items-center space-x-2">
              <Checkbox
                id={`rating-${rating}`}
                checked={minRating === rating}
                onCheckedChange={(checked) =>
                  setMinRating(checked ? rating : 0)
                }
              />
              <label
                htmlFor={`rating-${rating}`}
                className="text-xs sm:text-sm cursor-pointer flex items-center"
              >
                <div className="flex items-center">
                  {Array.from({ length: rating }).map((_, i) => (
                    <Star
                      key={i}
                      className="h-3 w-3 fill-yellow-400 text-yellow-400"
                    />
                  ))}
                  <span className="ml-1">& up</span>
                </div>
              </label>
            </div>
          ))}
        </div>
      </div> */
      }

      <Button
        variant="outline"
        onClick={clearAllFilters}
        className="w-full text-sm"
      >
        Clear All Filters
      </Button>
    </div>
  );

  return (
    <>
      <Header
        userId={userDetails?.id || ""}
        userName={userDetails?.first_name || ""}
        isAdmin={Boolean(userDetails?.admin)}
        category={selectedCategories}
      />
      <div className="min-h-screen bg-background">
        <div className="container mx-auto mt-16 px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8 max-w-full">
          <div className="flex flex-col lg:flex-row gap-4 lg:gap-6">
            {/* Desktop Filters Sidebar */}
            <div className="hidden lg:block w-64 flex-shrink-0">
              <div className="sticky top-24">
                <FilterSidebar />
              </div>
            </div>

            {/* Main Content */}
            <div className="flex-1 min-w-0 w-full">
              {/* Results Header */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4 sm:mb-6">
                <div className="flex-1 min-w-0">
                  <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold">
                    {resultsTitle}
                  </h1>
                  <p className="text-sm sm:text-base text-muted-foreground mt-1">
                    {searchView === "trending" &&
                      query.length === 0 &&
                      selectedCategories.length === 0 && (
                      <>
                        Recently popular wholesale listings
                        {filteredProducts.length > 0 && (
                          <>
                            {" "}
                            · {filteredProducts.length} products
                          </>
                        )}
                      </>
                    )}
                    {searchView === "specials" &&
                      query.length === 0 &&
                      selectedCategories.length === 0 && (
                      <>
                        Products with supplier markdown pricing
                        {filteredProducts.length > 0 && (
                          <>
                            {" "}
                            · {filteredProducts.length} products
                          </>
                        )}
                      </>
                    )}
                    {!searchView && query.length > 0 &&
                      selectedCategories.length === 0 && (
                      <>
                        Showing {filteredProducts.length} results for &quot;
                        {query}&quot;
                        {sortBy === "relevance" && query.trim() !== "" && (
                          <span className="ml-1">• Sorted by distance</span>
                        )}
                      </>
                    )}
                    {selectedCategories.length > 0 && (
                      <>
                        Showing {filteredProducts.length} results for{" "}
                        {selectedCategories}
                        {sortBy === "relevance" && query.trim() !== "" && (
                          <span className="ml-1">• Sorted by distance</span>
                        )}
                      </>
                    )}
                    {!searchView &&
                      query.length === 0 &&
                      selectedCategories.length === 0 && (
                      <>
                        Showing {filteredProducts.length} active products
                      </>
                    )}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0 w-full sm:w-auto">
                  {/* Mobile Filter Button */}
                  <Sheet>
                    <SheetTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className="lg:hidden flex-1 sm:flex-none"
                      >
                        <Filter className="h-4 w-4 mr-2" />
                        <span className="hidden sm:inline">Filters</span>
                      </Button>
                    </SheetTrigger>
                    <SheetContent
                      side="left"
                      className="w-[280px] sm:w-[320px] overflow-y-auto"
                    >
                      <SheetHeader className="pb-4">
                        <SheetTitle>Filters</SheetTitle>
                      </SheetHeader>
                      <div className="px-1">
                        <FilterSidebar />
                      </div>
                    </SheetContent>
                  </Sheet>

                  {/* Search Filter */}
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      type="text"
                      placeholder="Filter results..."
                      value={searchFilter}
                      onChange={(e) => setSearchFilter(e.target.value)}
                      className="pl-10 w-48 sm:w-56"
                    />
                  </div>

                  {/* Sort */}
                  <Select value={sortBy} onValueChange={setSortBy}>
                    <SelectTrigger className="w-full sm:w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="relevance">
                        {query.trim() !== "" ? "Distance" : "Relevance"}
                      </SelectItem>
                      <SelectItem value="price-low">
                        Price: Low to High
                      </SelectItem>
                      <SelectItem value="price-high">
                        Price: High to Low
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Active Filters */}
              {(initialQueryFromUrl.length > 0 ||
                searchView != null ||
                selectedCategories.length > 0 ||
                selectedSuppliers.length > 0 ||
                searchFilter.length > 0 ||
                // minRating > 0 ||
                maxDistance !== "0" ||
                inStockOnly) && (
                <div className="mb-4 sm:mb-6">
                  <div className="flex flex-wrap gap-1 sm:gap-2">
                    {searchView === "trending" && (
                      <Badge
                        variant="secondary"
                        className="cursor-pointer text-xs sm:text-sm"
                        onClick={() => navigate("/search?q=")}
                      >
                        Trending products ×
                      </Badge>
                    )}
                    {searchView === "specials" && (
                      <Badge
                        variant="secondary"
                        className="cursor-pointer text-xs sm:text-sm"
                        onClick={() => navigate("/search?q=")}
                      >
                        Supplier specials ×
                      </Badge>
                    )}
                    {initialQueryFromUrl.length > 0 &&
                      selectedCategories.length === 0 && (
                      <Badge
                        variant="secondary"
                        className="cursor-pointer text-xs sm:text-sm"
                        onClick={handleQueryClear}
                      >
                        {initialQueryFromUrl} ×
                      </Badge>
                    )}
                    {selectedCategories.length > 0 && (
                      <Badge
                        key={selectedCategories}
                        variant="secondary"
                        className="cursor-pointer text-xs sm:text-sm"
                        onClick={() => handleCategoryToggle("")}
                      >
                        {selectedCategories} ×
                      </Badge>
                    )}
                    {selectedSuppliers.map((supplier) => (
                      <Badge
                        key={supplier}
                        variant="secondary"
                        className="cursor-pointer text-xs sm:text-sm"
                        onClick={() => handleSupplierToggle(supplier)}
                      >
                        {supplier} ×
                      </Badge>
                    ))}
                    {maxDistance !== "0" && (
                      <Badge
                        variant="secondary"
                        className="cursor-pointer text-xs sm:text-sm"
                        onClick={() => setMaxDistance("0")}
                      >
                        Within {maxDistance} miles ×
                      </Badge>
                    )}
                    {
                      /* {minRating > 0 && (
                      <Badge
                        variant="secondary"
                        className="cursor-pointer text-xs sm:text-sm"
                        onClick={() => setMinRating(0)}
                      >
                        {minRating}+ Stars ×
                      </Badge>
                    )} */
                    }
                    {searchFilter.length > 0 && (
                      <Badge
                        variant="secondary"
                        className="cursor-pointer text-xs sm:text-sm"
                        onClick={() => setSearchFilter("")}
                      >
                        Filter: {searchFilter} ×
                      </Badge>
                    )}
                    {inStockOnly && (
                      <Badge
                        variant="secondary"
                        className="cursor-pointer text-xs sm:text-sm"
                        onClick={() => setInStockOnly(false)}
                      >
                        In Stock Only ×
                      </Badge>
                    )}
                  </div>
                  </div>
                )}

              {/* Results List */}
              {filteredProducts.length > 0
                ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-6 gap-3 sm:gap-4 lg:gap-5">
                    {paginatedProducts.map((product) => (
                      <ProductCard
                        key={product.id}
                        productId={product.id}
                        productDetails={product}
                        storeName={product.store_name}
                        size="promo"
                        fluid={true}
                      />
                    ))}
                  </div>
                )
                : (
                  <div className="text-center py-6 sm:py-8 lg:py-12">
                    <div className="text-muted-foreground mb-4">
                      <Search className="h-8 w-8 sm:h-12 sm:w-12 mx-auto mb-4 opacity-50" />
                      <h3 className="text-base sm:text-lg font-semibold mb-2">
                        No products found
                      </h3>
                      <p className="text-sm sm:text-base">
                        Try adjusting your search or filters
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      onClick={clearAllFilters}
                      className="w-full sm:w-auto"
                    >
                      Clear All Filters
                    </Button>
                  </div>
                )}

              {/* Pagination */}
              <Pagination />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
